import { Checklist } from "./Checklist";
import { SMS_GATEWAYS } from "./CustomerCommunication";
import { DealerLocation, NotifierKey } from "./DealerLocation";
import { DMS_CAPABILITIES } from "./Dms";
import { Domain } from "./Domain";
import { ExactFailedInvoice } from "./ExactFailedInvoice";
import { HoldingID } from "./Holding";
import { WARRANTY_TYPES } from "./Pin";

export type DealerID = number;

export type FeatureID = number;

export enum DEALER_REPORT_TEMPLATES {
  LocationOverview = 1,
  PercentWoWorkedOn = 2,
  PercentSoldItems = 3,
  RecurringCars = 4,
  LocationCommercialOverview = 5,
  QualityChecks = 6,
  CommunicationOverview = 7,
  OCIPerformances = 8,
  DOPerformances = 9,
  EmployeeLocationOverview = 10,
  CanceledAppointments = 11,
  LeasesOverview = 12,
  SnoozedItems = 13,
  SnoozedNextVisitCustomer = 14,
  SnoozedMakeOffer = 15,
  SnoozedMakeNewAppointment = 16,
  SnoozedRemindCustomer = 17,
  KeylockersOverview = 18,
  SMSOverview = 19,
  SMSEvents = 20,
  ShareboxOverview = 21,
  TyreOverview = 22,
  TyreteamOverview = 23,
  UserReport = 24,
  KioskOverview = 25
}

export enum DMS_QUOTA_STATUS {
  None = 1,
  Halfway = 50,
  Warning = 75,
  Critical = 90
}

export interface Dealer {
  created_on: string;
  updated_on: string;
  id: DealerID;
  name: string; // Name of the dealer
  country_code: string; // Country code associated with the dealer
  active: boolean; // Dealer is currently active
  can_add_users: boolean; // Dealer can add additional users
  accepted_api_agreement: boolean; // The dealer has accepted Claire's API agreement
  dbb_enabled: boolean; // Dealer has enabled DBB functionality
  custom_domain_enabled: boolean; // A custom domain is enabled for the dealer
  website_url: string; // The URL of the dealer's website
  sys_admin_name: string; // Name of the system administrator
  sys_admin_email: string; // Email address of the system administrator
  sys_admin_phone: string; // Phone number of the system administrator
  dms_server_info: string; // Information related to the dealer's physical machine running the DMS
  network_info: string; // Details about the dealer's network configuration
  city: string; // City where the dealer is located
  street: string; // Street address of the dealer
  region: string; // Region or state where the dealer is situated
  postal_code: string; // Postal code or ZIP code of the dealer's location
  phone_nr: string; // Phone number of the dealer
  domain_name: string; // Name of the dealer's domain
  enable_car_ready: boolean; // Car Ready' status feature is enabled
  customer_data_retention_in_years: number; // How long to wait before disposing of a customers information
  notifier_key: NotifierKey; // Authentication key used for the dealer level notifier websockets
  icar_last_update?: string; // Last time for this dealer that ICar processing occurred
  dms_quota_warning_reached: DMS_QUOTA_STATUS; // Scheduler DMS quota warning level reached
  sms_gateway_id: SMS_GATEWAYS; // Denotes which SMS Service the dealer is using
  twilio_account_sid: string; // Account SID for Twilio integration
  twilio_api_key: string; // API key for Twilio integration
  twilio_phone_number: string; // Phone number used by Twilio for contacting customers
  is_twilio_sub_account_managed: boolean; // Twilio sub-account is managed by Claire
  twilio_monthly_dollar_limit: number; // Twilio's monthly limit in dollars'
  twilio_monthly_dollar_limit_usage_trigger_sid: string; // Twilio Account SID to trigger monthly price limit overuse
  twilio_monthly_sms_limit: number; // Twilio's monthly sms limit'
  twilio_monthly_sms_limit_usage_trigger_sid: string; // Twilio Account SID to trigger monthly sms limit overuse
  twilio_daily_dollar_limit_usage_trigger_sid: string; // Twilio Account SID to trigger daily limit overuse
  planit_planning_enabled: boolean; // Indicates if PlanIt planning is enabled
  planit_planning_user_name: string; // Username for PlanIt planning
  onei_planning_enabled: boolean; // Indicates if Onei planning is enabled
  onei_planning_environment_guid: string; // Environment GUID for Onei planning
  web_clock_url: string; // URL for web clock functionality
  advised_critical_history_enabled: boolean; // Advised critical history is enabled
  is_snooze_type_next_visit_customer_enabled: boolean; // Next Visit Customer Snooze Type is enabled
  is_snooze_type_make_offer_enabled: boolean; // Make Offer Snooze Type is enabled
  is_snooze_type_make_new_appointment_enabled: boolean; // Make New Appointment Snooze Type is enabled
  is_snooze_type_remind_customer_enabled: boolean; // Remind Customer Snooze Type is enabled
  is_snooze_status_enabled: boolean; // Snooze Status is enabled
  is_snooze_department_enabled: boolean; // Snooze Department is enabled
  is_snooze_attachment_enabled: boolean; // Snooze Attachment is enabled
  is_pon_enabled: boolean; // Access to PON API is enabled
  pon_username: string; // Username to access PON API
  is_xentry_enabled: boolean; // XEntry is enabled
  exact_account_id?: string; // ID of the linked account in Exact Online
  is_exact_invoicing_enabled: boolean; // Use Exact invoice generation
  is_exact_dealer_startup_costs_invoiced: boolean; // Is the startup cost for this dealer already invoiced
  is_exact_onei_startup_costs_invoiced: boolean; // Is the Onei startup cost for this dealer already invoiced
  lef_username: string; // Username for LEF
  holding_id: HoldingID | null; // ID of the holding this dealer belongs to
  has_google_cloud_webhook_queue: boolean; // The dealer has a webhook queue in Google Cloud
  checklists?: Checklist[]; // Checklists created by this dealer
  locations?: DealerLocation[]; // Locations created by this dealer
  domain?: Domain; // Domain configured for this dealer
  dms_capability_ids?: DMS_CAPABILITIES[]; // DMS Capabilties of this dealer
  has_twilio_api_secret?: boolean; // API secret for Twilio integration has been set
  has_twilio_auth_token?: boolean; // Authentication token for Twilio integration has been set
  has_planit_planning_password?: boolean; // Password for PlanIt planning has been set
  has_onei_planning_api_key?: boolean; // API key for Onei planning has been set
  has_pon_password?: boolean; // Password for Pon has been set
  has_lef_password?: boolean; // Password for LEF has been set
  has_failed_invoices?: boolean; // Has failed Exact invoices
  has_kms_encrypted_xentry_client_id?: boolean; // ClientID for XEntry has been set
  has_kms_encrypted_xentry_client_secret?: boolean;
  exact_failed_invoices?: ExactFailedInvoice[]; // Exact failed Exact invoices of this dealer
  invoice_error?: string; // Last error message for Exact invoicing
  last_invoice_time?: string; // Last invoice time for Exact invoices
  enabled_warranty_types?: WARRANTY_TYPES[]; // List of warranty types enabled for this dealer
}

export interface DMSQuotaStatusDoc {
  none: DMS_QUOTA_STATUS;
  halfway: DMS_QUOTA_STATUS;
  warning: DMS_QUOTA_STATUS;
  critical: DMS_QUOTA_STATUS;
}

export interface DealerReportTemplate {
  created_on: string;
  id: DEALER_REPORT_TEMPLATES;
  filename: string;
  name: string;
  unit: string;
  thresholds: string;
  report_type_id: number;
  ordering: number;
  template_group: number;
  CC: number;
  report_type?: string;
}

export interface DealerReportData {
  id: DEALER_REPORT_TEMPLATES; // Identifier for the dealer report template
  report_type: string; // Type of the report
  report_name: string; // Name of the report
  unit: string; // Units to represent the report data
  thresholds: string; // Threshold values
  report_order: number; // Order of the report
  report_data?: string[];
}

export interface WarrantyTypeDealers {
  dealer_id: DealerID; // Dealer Identifier
  warranty_type_id: WARRANTY_TYPES; // Warranty Type Identifier
}
